import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, CAR_BRANDS, isValidPhone, errText, waLink, fmtDate } from "@/lib/api";
import { trackOtpVerified, trackPrizeWon, trackGrandPrizeWon, trackCouponClaimedWhatsapp, trackSpinStarted } from "@/lib/pixel";
import { SpinWheel, rotationForIndex } from "./SpinWheel";

export default function SpinSection() {
  const [stage, setStage] = useState("form"); // form | otp | ready | spinning | result | already
  const [form, setForm] = useState({ name: "", phone: "", car: "" });
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);

  const sendOtp = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!isValidPhone(form.phone)) return toast.error("Enter a valid 10-digit mobile number");
    if (!form.car) return toast.error("Select your car brand");
    trackSpinStarted({ source: "spin_form" });
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { phone: form.phone });
      setStage("otp");
      toast.success(`OTP sent to ${form.phone}`);
      if (data.mock) toast.info("Dev mode: use OTP 123456");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/otp/verify", { phone: form.phone, otp });
      setToken(data.verification_token);
      // Pass phone+name for advanced matching across Pixel + Google Ads
      trackOtpVerified({ source: "Spin Wheel", phone: form.phone, name: form.name });
      setStage("ready");
      toast.success("Verified! Spin the wheel 🎡");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const doSpin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/spin", { ...form, verification_token: token });
      setResult(data);
      if (data.already_rewarded) {
        setStage("already");
      } else {
        // Backend returns prize_index matching wheel segments (5-segment layout).
        setRotation(rotationForIndex(data.prize_index));
        setStage("spinning");
        // Fire analytics — generic + grand-prize event for high-value retargeting audience
        trackPrizeWon({ prize: data.prize, source: "Spin Wheel", car_brand: form.car, grand_prize: data.grand_prize });
        if (data.grand_prize) {
          trackGrandPrizeWon({ prize: data.prize, source: "Spin Wheel", car_brand: form.car });
        }
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const waMsg = result
    ? (result.grand_prize
        ? `Hi AutoHQ! 🎉 I just won the WEEKLY GRAND PRIZE — ${result.prize} — on the Spin & Win. My coupon code is ${result.coupon_code}. I'm ${form.name} and my car is a ${form.car}. Please confirm my free service slot.`
        : `Hi AutoHQ, I won ${result.prize}, my code is ${result.coupon_code}. I'm ${form.name} and my car is a ${form.car}.`)
    : "";

  return (
    <section id="spin" className="py-16 sm:py-28 px-5 lg:px-8 bg-[#F5F5F7] border-y border-[#E4E4E7]" data-testid="spin-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-3">Spin &amp; Win</p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
            Spin Once. <span className="text-[#FF5A00]">Win Guaranteed.</span>
          </h2>
          <p className="mt-3 text-[#52525B]">Every spin wins a real reward. Verify your number, spin, and collect your coupon.</p>
          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.2em] font-semibold" data-testid="spin-grand-prize-badge">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            <span className="text-[#D4AF37]">Grand Prize</span>
            <span className="text-white/80 normal-case tracking-normal font-medium">— win a FREE car service worth ₹5,000</span>
          </div>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 sm:p-8">
            {stage === "form" && (
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Your Name</Label>
                  <Input data-testid="spin-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Ravi Kumar" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A] focus:border-[#FF5A00]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Mobile Number</Label>
                  <Input data-testid="spin-phone-input" value={form.phone} maxLength={10} inputMode="numeric"
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                    placeholder="10-digit mobile number" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A] focus:border-[#FF5A00]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Car Brand &amp; Model</Label>
                  <Select value={form.car} onValueChange={(v) => setForm({ ...form, car: v })}>
                    <SelectTrigger data-testid="spin-car-select" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A]">
                      <SelectValue placeholder="Select your car brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAR_BRANDS.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="spin-send-otp-button" onClick={sendOtp} disabled={busy}
                  className="w-full bg-[#FF5A00] hover:bg-[#E04F00] font-bold py-6 text-base">
                  {busy ? "Sending OTP..." : "Verify & Unlock My Spin"}
                </Button>
              </div>
            )}

            {stage === "otp" && (
              <div className="space-y-5">
                <p className="text-sm text-[#52525B]">Enter the 6-digit OTP sent to <span className="text-white font-bold">{form.phone}</span></p>
                <div className="flex justify-center" data-testid="spin-otp-input">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="bg-white border-[#E4E4E7] text-[#0A0A0A] w-11 h-12" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button data-testid="spin-verify-otp-button" onClick={verifyOtp} disabled={busy}
                  className="w-full bg-[#FF5A00] hover:bg-[#E04F00] font-bold py-6 text-base">
                  {busy ? "Verifying..." : "Verify OTP"}
                </Button>
                <button data-testid="spin-back-button" onClick={() => setStage("form")} className="text-sm text-[#71717A] hover:text-[#0A0A0A] w-full">
                  ← Change number
                </button>
              </div>
            )}

            {(stage === "ready" || stage === "spinning") && (
              <div className="text-center space-y-5">
                <p className="text-lg font-bold">You're verified, {form.name.split(" ")[0]}! 🎉</p>
                <p className="text-sm text-[#52525B]">One spin. Guaranteed reward. Make it count.</p>
                <Button data-testid="spin-now-button" onClick={doSpin} disabled={busy || stage === "spinning"}
                  className="w-full bg-[#FF5A00] hover:bg-[#E04F00] font-bold py-6 text-lg">
                  {stage === "spinning" ? "Spinning..." : "🎡 SPIN NOW"}
                </Button>
              </div>
            )}

            {stage === "result" && result && (
              <div className="text-center space-y-4" data-testid="spin-result">
                {result.grand_prize ? (
                  <>
                    <p className="text-sm uppercase tracking-[0.2em] text-[#D4AF37] font-bold">🏆 Grand Prize</p>
                    <p className="text-3xl font-extrabold text-[#0A0A0A]">{result.prize}</p>
                    <p className="text-xs text-[#52525B] -mt-1">Congratulations {form.name.split(" ")[0]} — you just won a FREE car service!</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm uppercase tracking-[0.2em] text-[#FF5A00] font-bold">You Won</p>
                    <p className="text-3xl font-extrabold">{result.prize}</p>
                  </>
                )}
                <div className={`coupon-box rounded-lg py-3 px-4 font-mono text-xl font-bold ${result.grand_prize ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#FF5A00]"}`} data-testid="spin-coupon-code">
                  {result.coupon_code}
                </div>
                <p className="text-xs text-[#71717A]">Valid till {fmtDate(result.expires_at)} · Code sent via SMS</p>
                <Button asChild className="w-full bg-[#25D366] hover:bg-[#1fb457] font-bold py-6">
                  <a data-testid="spin-whatsapp-button" href={waLink(waMsg)} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackCouponClaimedWhatsapp({ prize: result.prize, source: "Spin Wheel", coupon_code: result.coupon_code })}>
                    Claim on WhatsApp
                  </a>
                </Button>
              </div>
            )}

            {stage === "already" && result && (
              <div className="text-center space-y-4" data-testid="spin-already-rewarded">
                <p className="text-lg font-bold">You've already got a reward waiting! 🎁</p>
                <p className="text-sm text-[#52525B]">{result.prize}</p>
                <div className="coupon-box rounded-lg py-3 px-4 font-mono text-xl font-bold text-[#FF5A00]">
                  {result.coupon_code}
                </div>
                <p className="text-xs text-[#71717A]">Valid till {fmtDate(result.expires_at)}</p>
                <Button asChild className="w-full bg-[#25D366] hover:bg-[#1fb457] font-bold py-6">
                  <a href={waLink(`Hi AutoHQ, I'd like to book a slot to redeem my code ${result.coupon_code}. I'm ${form.name} and my car is a ${form.car}.`)} target="_blank" rel="noopener noreferrer">
                    Book a Slot to Redeem
                  </a>
                </Button>
              </div>
            )}

            <p className="mt-6 text-xs text-[#A1A1AA] text-center">
              One reward per mobile number. Valid 14 days at AutoHQ Ranigunj.
            </p>
          </div>

          <div className="order-first lg:order-last">
            <SpinWheel
              rotation={rotation}
              spinning={stage === "spinning"}
              onComplete={() => setStage("result")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
